import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Inventory2 from '@mui/icons-material/esm/Inventory2';
import Warning from '@mui/icons-material/esm/Warning';
import LocalShipping from '@mui/icons-material/esm/LocalShipping';
import BuildIcon from '@mui/icons-material/esm/Build';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import resourceService, { InventorySummaryRow } from '../../services/api/resourceService';

const RESOURCE_TYPE_LABEL: Record<string, string> = {
  material: 'Material',
  tool: 'Tool',
  equipment: 'Equipment',
  supply: 'Supply',
  labour: 'Labour',
};

export default function ResourcesPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<InventorySummaryRow[]>([]);
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [lowStockOnly, setLowStockOnly] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await resourceService.getInventorySummary({
        resourceType: resourceTypeFilter === 'all' ? undefined : resourceTypeFilter,
        locationId: locationFilter === 'all' ? undefined : locationFilter,
        lowStockOnly,
      });
      setRows(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load inventory summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceTypeFilter, locationFilter, lowStockOnly]);

  const locations = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (!seen.has(r.storageLocationId)) seen.set(r.storageLocationId, `${r.storageLocationCode} — ${r.storageLocationName}`);
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [rows]);

  const summary = useMemo(() => {
    const totalSkus = new Set(rows.map((r) => r.resourceId)).size;
    const lowStock = rows.filter((r) => r.lowStock).length;
    const belowSafety = rows.filter((r) => r.belowSafetyStock).length;
    const inventoryValue = rows.reduce((sum, r) => sum + (r.unitCost ?? 0) * r.quantityAvailable, 0);
    return { totalSkus, totalRows: rows.length, lowStock, belowSafety, inventoryValue };
  }, [rows]);

  const columns: Column<InventorySummaryRow>[] = [
    { id: 'resourceCode', label: 'Code', minWidth: 110 },
    { id: 'resourceName', label: 'Name', minWidth: 200 },
    {
      id: 'resourceType',
      label: 'Type',
      minWidth: 100,
      format: (value: string) => RESOURCE_TYPE_LABEL[value] ?? value,
    },
    { id: 'storageLocationCode', label: 'Location', minWidth: 110 },
    { id: 'unitOfMeasure', label: 'UoM', minWidth: 70 },
    {
      id: 'quantityAvailable',
      label: 'Available',
      minWidth: 100,
      align: 'right',
      format: (value: number, row: InventorySummaryRow) => (
        <Typography
          variant="body2"
          fontWeight={row.lowStock ? 'bold' : 'normal'}
          color={row.belowSafetyStock ? 'error.main' : row.lowStock ? 'warning.main' : 'text.primary'}
        >
          {value.toLocaleString()}
        </Typography>
      ),
    },
    { id: 'quantityReserved', label: 'Reserved', minWidth: 90, align: 'right' },
    { id: 'quantityInUse', label: 'In Use', minWidth: 80, align: 'right' },
    {
      id: 'reorderLevel',
      label: 'Reorder',
      minWidth: 90,
      align: 'right',
      format: (value: number | null) => (value !== null ? value.toLocaleString() : '—'),
    },
    {
      id: 'lowStock',
      label: 'Status',
      minWidth: 130,
      format: (_value: boolean, row: InventorySummaryRow) => (
        row.belowSafetyStock ? (
          <Chip size="small" label="Below safety" color="error" />
        ) : row.lowStock ? (
          <Chip size="small" label="Low stock" color="warning" />
        ) : (
          <Chip size="small" label="OK" color="success" />
        )
      ),
    },
    {
      id: 'unitCost',
      label: 'Unit Cost',
      minWidth: 100,
      align: 'right',
      format: (value: number | null) => (value !== null ? `$${value.toLocaleString()}` : '—'),
    },
  ];

  return (
    <Box>
      <PageHeader title="Resources / Inventory" subtitle={`${summary.totalSkus} SKUs across ${locations.length} location${locations.length === 1 ? '' : 's'}`} />

      {/* Summary KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Total SKUs" value={`${summary.totalSkus}`} icon={<Inventory2 />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Low Stock"
            value={`${summary.lowStock}`}
            changeLabel={summary.lowStock === 0 ? 'All resources above reorder level' : 'Below reorder level — restock'}
            icon={<Warning />}
            color={summary.lowStock > 0 ? 'warning' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Below Safety Stock"
            value={`${summary.belowSafety}`}
            changeLabel={summary.belowSafety === 0 ? 'No critical shortages' : 'Critical — risk of work stoppage'}
            icon={<BuildIcon />}
            color={summary.belowSafety > 0 ? 'error' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Inventory Value (available)"
            value={`$${summary.inventoryValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            changeLabel="At captured unit cost"
            icon={<LocalShipping />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Resource type</InputLabel>
          <Select value={resourceTypeFilter} label="Resource type" onChange={(e) => setResourceTypeFilter(e.target.value)}>
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="material">Material</MenuItem>
            <MenuItem value="tool">Tool</MenuItem>
            <MenuItem value="equipment">Equipment</MenuItem>
            <MenuItem value="supply">Supply</MenuItem>
            <MenuItem value="labour">Labour</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Storage location</InputLabel>
          <Select value={locationFilter} label="Storage location" onChange={(e) => setLocationFilter(e.target.value)}>
            <MenuItem value="all">All locations</MenuItem>
            {locations.map((l) => (
              <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} />}
          label="Low stock only"
        />
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">No inventory rows match the current filters.</Alert>
      ) : (
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <DataTable columns={columns} data={rows} pagination searchable />
        </Box>
      )}
    </Box>
  );
}
